import { createContext, createElement, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./auth-session";

export type Role = "admin" | "editor" | "freelancer";

export const ROLES: { id: Role; label: string; hint: string }[] = [
  { id: "admin", label: "Admin", hint: "Team, data health, analytics, sending" },
  { id: "editor", label: "Editor", hint: "Audiences, review, campaigns, and freelancer invites" },
  { id: "freelancer", label: "Freelancer", hint: "Write and design newsletters" },
];

const STORAGE_KEY = "nl-role";

export type Action =
  | "segments.view"
  | "segments.write"
  | "campaigns.write"
  | "newsletters.write"
  | "newsletters.approve"
  | "settings.write"
  | "pipeline.view"
  | "subscribers.view"
  | "team.view"
  | "team.invite"
  | "team.password"
  | "health.view"
  | "analytics.view";

export function roleCan(role: Role, action: Action): boolean {
  return PERMISSIONS[role].includes(action);
}

/** Who the current user is allowed to add. Editors: freelancers only. Admins: admins, editors, and freelancers. */
export function invitableRoles(actor: Role): Role[] {
  if (actor === "admin") return ["admin", "editor", "freelancer"];
  if (actor === "editor") return ["freelancer"];
  return [];
}

const PERMISSIONS: Record<Role, Action[]> = {
  admin: [
    "segments.view", "segments.write", "campaigns.write", "newsletters.write", "newsletters.approve",
    "settings.write", "pipeline.view", "subscribers.view",
    "team.view", "team.invite", "team.password", "health.view", "analytics.view",
  ],
  editor: ["segments.view", "segments.write", "newsletters.write", "newsletters.approve", "campaigns.write", "analytics.view", "team.view", "team.invite"],
  freelancer: ["newsletters.write"],
};

const RoleContext = createContext<{
  role: Role;
  setRole: (role: Role) => void;
  signedIn: boolean;
  demoSwitch: boolean;
} | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [demoRole, setDemoRole] = useState<Role>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "editor" || saved === "freelancer" || saved === "admin" ? saved : "admin";
  });

  useEffect(() => {
    if (!session.me) localStorage.setItem(STORAGE_KEY, demoRole);
  }, [demoRole, session.me]);

  const value = useMemo(() => ({
    role: session.me?.role ?? demoRole,
    setRole: session.me ? () => undefined : setDemoRole,
    signedIn: !!session.me,
    demoSwitch: !session.configured || !session.me,
  }), [session.me, session.configured, demoRole]);

  return createElement(RoleContext.Provider, { value }, children);
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used inside RoleProvider");
  return ctx;
}
